import { useState } from 'react';
import { AlertCircle, AlertTriangle, CheckCircle2, HelpCircle } from 'lucide-react';
import { groupFlagsBySeverity } from '../../utils/clinicalFlags';
import { useLanguage } from '../../contexts/LanguageContext';
import { pluralKey } from '../../i18n/translations';

function SeverityIcon({ severity }) {
  if (severity === 'critical' || severity === 'high_alert') {
    return <AlertCircle className="h-4 w-4 shrink-0 text-[#DC2626]" strokeWidth={2} />;
  }
  if (severity === 'concern' || severity === 'warning') {
    return <AlertTriangle className="h-4 w-4 shrink-0 text-[#EA580C]" strokeWidth={2} />;
  }
  return <HelpCircle className="h-4 w-4 shrink-0 text-gray-500" strokeWidth={2} />;
}

function FlagItem({ flag }) {
  return (
    <li className="flex items-start gap-2 py-1.5">
      <SeverityIcon severity={flag.severity} />
      <div className="min-w-0">
        <p className="text-sm text-gray-900">{flag.message}</p>
        {flag.value && <p className="mt-0.5 text-xs text-gray-500">{flag.value}</p>}
      </div>
    </li>
  );
}

function CollapsibleGroup({ title, flags, className, borderClass, defaultOpen = false, t }) {
  const [open, setOpen] = useState(defaultOpen);
  if (!flags.length) return null;

  return (
    <div className={`rounded-lg border-l-4 ${borderClass} ${className}`}>
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className="flex w-full items-center justify-between px-3 py-2 text-left text-sm font-semibold"
      >
        <span>{title}</span>
        <span className="text-xs font-medium opacity-80">{open ? t('hide') : t('show')}</span>
      </button>
      {open && (
        <ul className="border-t border-black/5 px-3 py-1">
          {flags.map((flag, index) => (
            <FlagItem key={`${flag.message}-${index}`} flag={flag} />
          ))}
        </ul>
      )}
    </div>
  );
}

export default function ClinicalFlagsPanel({ flags = [] }) {
  const { t } = useLanguage();
  const groups = groupFlagsBySeverity(flags);
  const hasFlags =
    groups.urgent.length ||
    groups.concern.length ||
    groups.warning.length ||
    groups.verify.length;

  if (!hasFlags) return null;

  return (
    <div className="mt-4 space-y-2">
      {groups.urgent.length > 0 && (
        <div className="rounded-lg border-l-4 border-[#DC2626] bg-[#FEF2F2] px-3 py-2">
          <p className="mb-1 text-sm font-semibold text-[#DC2626]">{t('criticalHighAlert')}</p>
          <ul>
            {groups.urgent.map((flag, index) => (
              <FlagItem key={`urgent-${index}`} flag={flag} />
            ))}
          </ul>
        </div>
      )}

      <CollapsibleGroup
        title={pluralKey(t, groups.concern.length, 'concernsCount', 'concernsCountPlural')}
        flags={groups.concern}
        className="bg-[#FFF7ED]"
        borderClass="border-[#EA580C]"
        t={t}
      />

      <CollapsibleGroup
        title={pluralKey(t, groups.warning.length, 'warningsCount', 'warningsCountPlural')}
        flags={groups.warning}
        className="bg-[#FEF3C7]"
        borderClass="border-[#D97706]"
        t={t}
      />

      <CollapsibleGroup
        title={pluralKey(t, groups.verify.length, 'verifyCount', 'verifyCountPlural')}
        flags={groups.verify}
        className="bg-[#FFFBEB]"
        borderClass="border-gray-400"
        t={t}
      />
    </div>
  );
}

export function AdverseReactionBanner({ flags }) {
  const adverse = flags.filter(
    (flag) => flag.category === 'adverse_reaction' && flag.severity === 'critical'
  );
  if (!adverse.length) return null;

  return (
    <div className="shrink-0 rounded-t-[24px] bg-[#DC2626] px-4 py-3 text-sm font-semibold text-white" role="alert">
      {adverse.map((flag, index) => (
        <p key={`adverse-${index}`}>{flag.message}</p>
      ))}
    </div>
  );
}

export function HighAlertBanner({ flags }) {
  const { t } = useLanguage();
  const hasHighAlert = flags.some((flag) => flag.severity === 'high_alert');
  if (!hasHighAlert) return null;

  return (
    <div
      className="shrink-0 border-b border-red-200 bg-[#FEF2F2] px-4 py-3 text-sm font-semibold text-[#DC2626]"
      role="alert"
    >
      ⚠️ {t('highAlertBanner')}
    </div>
  );
}

export function VerifiedBadge() {
  const { t } = useLanguage();
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-green-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-green-700">
      <CheckCircle2 className="h-3 w-3" />
      {t('verified')}
    </span>
  );
}
